/**
 * Question trees + answer maps + visited groups + question-fetch status.
 *
 * All "Q&A" state lives here so a junior touching the questions flow has
 * one file to open:
 *   - questionGroups          — speciality master tree (rendered on
 *                                Classification + License pages). The
 *                                /questions response already includes
 *                                group `id`s, so this also serves as
 *                                `questionGroupsWithIds` (aliased getter
 *                                kept for back-compat with consumers).
 *   - submissionQuestionGroups — submission-scoped tree returned by
 *                                /submission-questions; used on Coverage
 *                                + Underwriting pages.
 *   - questionAnswers / coverageAnswers / impactAnswers / underwritingAnswers
 *     — answer maps keyed by question id.
 *   - visitedQuestionGroups   — names of groups the user actually landed on,
 *                                so the signup request saves only those.
 *
 * Status flags for the corresponding fetches live here too.
 */
class QuestionsStore {
  #listeners = new Set();

  // ── Question trees ─────────────────────────────────────────────────────
  #questionGroups = [];
  #submissionQuestionGroups = [];

  // Cache marker — which speciality id `questionGroups` belongs to. Lets
  // useQuestionsAutoLoad short-circuit when the cached tree is for the
  // requested spec id (avoids a duplicate fetch on remount).
  #questionGroupsSpecId = null;

  get questionGroups() { return this.#questionGroups; }
  set questionGroups(v) { this.#questionGroups = v; this.#notify(); }

  get questionGroupsSpecId() { return this.#questionGroupsSpecId; }
  setQuestionGroupsForSpec(specId, data) {
    this.#questionGroups = Array.isArray(data) ? data : [];
    this.#questionGroupsSpecId = specId == null ? null : String(specId);
    this.#notify();
  }
  getQuestionGroupsBySpec(specId) {
    return this.#questionGroupsSpecId === String(specId) ? this.#questionGroups : null;
  }

  // Alias — /questions ships group ids inline now, so this just returns
  // the master tree. Setter is a no-op kept for legacy call sites.
  get questionGroupsWithIds() { return this.#questionGroups; }
  set questionGroupsWithIds(_v) { /* no-op: see getter */ }

  get submissionQuestionGroups() { return this.#submissionQuestionGroups; }
  set submissionQuestionGroups(v) { this.#submissionQuestionGroups = v; this.#notify(); }

  // Submission id the cached `submissionQuestionGroups` belong to. The tree
  // isn't wiped when the user resumes a different submission, so callers
  // compare this marker against the active sid to know when the cache is
  // stale and a refetch is needed (without it, resuming submission B after A
  // would keep showing A's answers).
  #submissionQuestionsSid = null;

  get submissionQuestionsSid() { return this.#submissionQuestionsSid; }
  set submissionQuestionsSid(v) { this.#submissionQuestionsSid = v == null ? null : String(v); this.#notify(); }

  // ── Answer maps (keyed by question id) ────────────────────────────────
  #questionAnswers = {};
  #coverageAnswers = {};
  #impactAnswers = {};
  #underwritingAnswers = {};

  get questionAnswers() { return this.#questionAnswers; }
  set questionAnswers(v) { this.#questionAnswers = v; this.#notify(); }

  get coverageAnswers() { return this.#coverageAnswers; }
  set coverageAnswers(v) { this.#coverageAnswers = v; this.#notify(); }

  get impactAnswers() { return this.#impactAnswers; }
  set impactAnswers(v) { this.#impactAnswers = v; this.#notify(); }

  get underwritingAnswers() { return this.#underwritingAnswers; }
  set underwritingAnswers(v) { this.#underwritingAnswers = v; this.#notify(); }

  // ── Question Visibility (Hide/Show) — submission tree ─────────────────
  // hiddenQuestionIds: the live hidden set for `submissionQuestionGroups`.
  // Read-only getter; the ONLY writer is setHiddenQuestionIds, which also
  // enforces FR4 ("un-hiding never restores a previous answer") by dropping
  // any newly-hidden id from all three answer maps atomically.
  // showTargetQuestionIds: structural — every Show rule's target; seeded once
  // per tree-load, not recomputed on every answer change.
  #hiddenQuestionIds = new Set();
  #showTargetQuestionIds = new Set();

  get hiddenQuestionIds() { return this.#hiddenQuestionIds; }

  setHiddenQuestionIds(nextHidden) {
    const next = nextHidden instanceof Set ? nextHidden : new Set();
    const newlyHidden = [...next].filter((id) => !this.#hiddenQuestionIds.has(id));
    if (newlyHidden.length > 0) {
      const drop = (map) => {
        let changed = false;
        const copy = { ...map };
        for (const id of newlyHidden) {
          const key = String(id);
          if (Object.prototype.hasOwnProperty.call(copy, key)) { delete copy[key]; changed = true; }
        }
        return changed ? copy : map;
      };
      this.#questionAnswers = drop(this.#questionAnswers);
      this.#coverageAnswers = drop(this.#coverageAnswers);
      this.#underwritingAnswers = drop(this.#underwritingAnswers);
    }
    this.#hiddenQuestionIds = next;
    this.#notify();
  }

  get showTargetQuestionIds() { return this.#showTargetQuestionIds; }
  set showTargetQuestionIds(v) { this.#showTargetQuestionIds = v instanceof Set ? v : new Set(); this.#notify(); }

  // ── Question Visibility (Hide/Show) — master/pre-submission tree ───────
  // A second, INDEPENDENT pair: master `question.id` and `submission_question.id`
  // are different id sequences with no guaranteed disjointness, so folding both
  // trees' hidden ids into one Set could hide the wrong question on the wrong
  // page. setMasterHiddenQuestionIds enforces the same FR4 contract, scoped to
  // `#questionAnswers` (the only answer map any master-tree page writes to).
  #masterHiddenQuestionIds = new Set();
  #masterShowTargetQuestionIds = new Set();

  get masterHiddenQuestionIds() { return this.#masterHiddenQuestionIds; }
  setMasterHiddenQuestionIds(nextHidden) {
    const next = nextHidden instanceof Set ? nextHidden : new Set();
    const newlyHidden = [...next].filter((id) => !this.#masterHiddenQuestionIds.has(id));
    if (newlyHidden.length > 0) {
      const copy = { ...this.#questionAnswers };
      let changed = false;
      for (const id of newlyHidden) {
        const key = String(id);
        if (Object.prototype.hasOwnProperty.call(copy, key)) { delete copy[key]; changed = true; }
      }
      if (changed) this.#questionAnswers = copy;
    }
    this.#masterHiddenQuestionIds = next;
    this.#notify();
  }

  get masterShowTargetQuestionIds() { return this.#masterShowTargetQuestionIds; }
  set masterShowTargetQuestionIds(v) { this.#masterShowTargetQuestionIds = v instanceof Set ? v : new Set(); this.#notify(); }

  // ── Visited groups (pre-signup save uses these to scope the payload) ──
  #visitedQuestionGroups = [];

  get visitedQuestionGroups() { return this.#visitedQuestionGroups; }
  set visitedQuestionGroups(v) { this.#visitedQuestionGroups = v; this.#notify(); }

  /** Record a group the user landed on. No-op if already recorded. */
  markGroupVisited(groupName) {
    const name = String(groupName || "").trim();
    if (!name) return;
    if (this.#visitedQuestionGroups.includes(name)) return;
    this.#visitedQuestionGroups = [...this.#visitedQuestionGroups, name];
    this.#notify();
  }

  // ── Fetch status flags ─────────────────────────────────────────────────
  #questionsLoading = false;
  #questionsError = null;
  #submissionQuestionsLoading = false;
  #submissionQuestionsError = null;
  #specialityLoadError = null;
  #underwritingSaving = false;
  #underwritingSaveError = null;

  get questionsLoading() { return this.#questionsLoading; }
  set questionsLoading(v) { this.#questionsLoading = !!v; this.#notify(); }

  get questionsError() { return this.#questionsError; }
  set questionsError(v) { this.#questionsError = v; this.#notify(); }

  get submissionQuestionsLoading() { return this.#submissionQuestionsLoading; }
  set submissionQuestionsLoading(v) { this.#submissionQuestionsLoading = !!v; this.#notify(); }

  get submissionQuestionsError() { return this.#submissionQuestionsError; }
  set submissionQuestionsError(v) { this.#submissionQuestionsError = v; this.#notify(); }

  get specialityLoadError() { return this.#specialityLoadError; }
  set specialityLoadError(v) { this.#specialityLoadError = v; this.#notify(); }

  get underwritingSaving() { return this.#underwritingSaving; }
  set underwritingSaving(v) { this.#underwritingSaving = !!v; this.#notify(); }

  get underwritingSaveError() { return this.#underwritingSaveError; }
  set underwritingSaveError(v) { this.#underwritingSaveError = v; this.#notify(); }

  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify() { this.#listeners.forEach((l) => l()); }

  clear() {
    this.#questionGroups = [];
    // Reset the spec cache marker too. Leaving it set while `#questionGroups`
    // is emptied poisons `getQuestionGroupsBySpec` (which returns the empty —
    // but truthy — array), so the Classification page's fetch guard thinks the
    // tree is still cached and never refetches on the next quote → blank page.
    this.#questionGroupsSpecId = null;
    this.#submissionQuestionGroups = [];
    this.#submissionQuestionsSid = null;
    this.#questionAnswers = {};
    this.#coverageAnswers = {};
    this.#impactAnswers = {};
    this.#underwritingAnswers = {};
    this.#hiddenQuestionIds = new Set();
    this.#showTargetQuestionIds = new Set();
    this.#masterHiddenQuestionIds = new Set();
    this.#masterShowTargetQuestionIds = new Set();
    this.#visitedQuestionGroups = [];
    this.#questionsLoading = false;
    this.#questionsError = null;
    this.#submissionQuestionsLoading = false;
    this.#submissionQuestionsError = null;
    this.#specialityLoadError = null;
    this.#underwritingSaving = false;
    this.#underwritingSaveError = null;
    this.#notify();
  }
}

const questionsStore = new QuestionsStore();
export default questionsStore;
